// ── Fetch interceptor for tenant routing ─────────────────────────────────
// API requests go to api.reruptionchat.com regardless of which domain the
// browser is on, so the backend can't determine the tenant from the Host
// header alone. This interceptor injects an X-Tenant-Slug header into
// every fetch() call, derived from either:
//   1. sessionStorage "impersonate_tenant" (platform admin entered a tenant)
//   2. The subdomain of the current browser URL (e.g. acme.localhost)
// The backend tenant middleware checks X-Tenant-Slug before any other
// resolution method, so this takes priority.
//
// For custom domains (not a subdomain of BASE_DOMAIN), we can't derive a
// slug from the hostname. Instead we store the resolved slug in
// sessionStorage after the first successful /api/resolve-tenant call
// (see _setCustomDomainSlug below). On the very first request we send
// X-Custom-Domain so the backend can resolve the tenant by domain.
//
// Loaded from index.js before AppWrapper so module side effects stay out of
// the React tree — avoids Fast Refresh TDZ issues on AppWrapper's default export.

// Subdomains used by platform infra — never tenant slugs.
// Must match backend's config.RESERVED_SUBDOMAINS.
const RESERVED_SUBDOMAINS = new Set([
  'admin', 'api', 'widget', 'app', 'www', 'staging',
]);

function _resolveSlugFromHostname() {
  const hostname = window.location.hostname;

  // Local dev: "acme.localhost" → "acme"
  if (hostname.endsWith('.localhost') || hostname.endsWith('.127.0.0.1')) {
    const sub = hostname.split('.')[0];
    if (sub && RESERVED_SUBDOMAINS.has(sub)) return null;
    return sub || null;
  }

  // Production / staging: "acme.reruptionchat.com" → "acme"
  const baseDomain = process.env.REACT_APP_BASE_DOMAIN;
  if (baseDomain && hostname.endsWith('.' + baseDomain)) {
    const sub = hostname.slice(0, -(baseDomain.length + 1));
    // Only single-level subdomains are valid tenant slugs
    if (sub && !sub.includes('.') && !RESERVED_SUBDOMAINS.has(sub)) return sub;
  }

  return null;
}

// Returns true when the browser hostname is a custom domain (not our
// base domain, not a subdomain of it, and not localhost).
function _isCustomDomain() {
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return false;
  if (hostname.endsWith('.localhost') || hostname.endsWith('.127.0.0.1')) return false;
  const baseDomain = process.env.REACT_APP_BASE_DOMAIN;
  if (!baseDomain) return false;
  return hostname !== baseDomain && !hostname.endsWith('.' + baseDomain);
}

// Store/retrieve the resolved slug for custom domains so subsequent
// API calls include the correct X-Tenant-Slug header.
// Exposed on window so TenantContext can call it after resolving.
function _setCustomDomainSlug(slug) {
  sessionStorage.setItem('custom_domain_slug', slug);
}
window._setCustomDomainSlug = _setCustomDomainSlug;

function _getCustomDomainSlug() {
  return sessionStorage.getItem('custom_domain_slug');
}

const _originalFetch = window.fetch;
window.fetch = function (url, options = {}) {
  // Priority: impersonate > subdomain slug > cached custom domain slug
  const slug = sessionStorage.getItem('impersonate_tenant')
    || _resolveSlugFromHostname()
    || (_isCustomDomain() ? _getCustomDomainSlug() : null);

  if (slug) {
    if (options.headers instanceof Headers) {
      options.headers.set('X-Tenant-Slug', slug);
    } else {
      options = {
        ...options,
        headers: {
          ...(options.headers || {}),
          'X-Tenant-Slug': slug,
        },
      };
    }
  } else if (_isCustomDomain()) {
    // First request on a custom domain — no slug cached yet.
    // Send X-Custom-Domain so the backend can resolve the tenant by domain.
    const customDomain = window.location.hostname;
    if (options.headers instanceof Headers) {
      options.headers.set('X-Custom-Domain', customDomain);
    } else {
      options = {
        ...options,
        headers: {
          ...(options.headers || {}),
          'X-Custom-Domain': customDomain,
        },
      };
    }
  }
  const requestUrl = typeof url === 'string' ? url : (url && url.url) || '';
  return _originalFetch.call(this, url, options).then((res) => {
    // Global "session may be gone" signal. useAuth listens, re-verifies the
    // session with the backend and logs out locally only if it's really
    // invalid — so unrelated 401s can't kick the user out. /auth/* calls are
    // skipped: login/verify/logout report 401 as a normal result.
    if (res.status === 401 && !requestUrl.includes('/auth/')) {
      window.dispatchEvent(new Event('auth:unauthorized'));
    }
    return res;
  });
};
